import React, { useState, useEffect, useCallback } from 'react';
import { fetchData } from '../api/restapi';
import realTimeDeviceService from '../../services/realTimeDeviceService';
import ConfirmationDialog from '../popups/ConfirmationDialog';
import RTActionEditor from '../tabs/RTActionEditor';
import Popup from '../popups/popup';
import { formatLocalDateTime, formatElapsed } from '../utils/formatDateTime';
import '../assets/AllTabsCommon.css';
import '../assets/RemoteDebugSessionTabs.css';
import '../assets/NdSessionView.css';

const PAGE_SIZE = 10;
const ALL_STATUSES = ['Active', 'Scheduled', 'Completed'];

const STATUS_META = {
  Active:    { badge: 'nd-sv-badge nd-sv-badge--active',    icon: '●', label: 'Active'    },
  Scheduled: { badge: 'nd-sv-badge nd-sv-badge--scheduled', icon: '⏳', label: 'Scheduled' },
  Completed: { badge: 'nd-sv-badge nd-sv-badge--completed', icon: '✓', label: 'Completed' },
  Cancelled: { badge: 'nd-sv-badge nd-sv-badge--cancelled', icon: '✕', label: 'Cancelled' },
};

function statusOf(session) {
  const s = (session.status || '').toLowerCase();
  if (s === 'running' || s === 'active') return 'Active';
  if (s === 'scheduled') return 'Scheduled';
  if (s === 'cancelled' || s === 'canceled') return 'Cancelled';
  return 'Completed';
}

function StatusBadge({ session }) {
  const st = statusOf(session);
  const m = STATUS_META[st] || STATUS_META.Completed;
  return <span className={m.badge}>{m.icon} {m.label}</span>;
}

function ConfigChips({ session }) {
  const c = session.actionTypeCounts;
  if (!c) return <span className="rt-text-muted">{session.configSummary || '—'}</span>;
  return (
    <div className="rt-config-chips">
      {c.monitor > 0 && <span className="rt-chip rt-chip-blue">👁 {c.monitor}</span>}
      {c.configure > 0 && <span className="rt-chip rt-chip-purple">⚙ {c.configure}</span>}
      {c.run > 0 && <span className="rt-chip rt-chip-green">⚡ {c.run}</span>}
    </div>
  );
}

function NdRtViewSessions({ userInfo, onStartSession, prefilledSearch = '' }) {
  const [activeSessions, setActiveSessions]       = useState([]);
  const [scheduledSessions, setScheduledSessions] = useState([]);
  const [completedSessions, setCompletedSessions] = useState([]);
  const [loading, setLoading]                     = useState(true);

  // Per-tab pagination totals
  const [activePagination, setActivePagination]       = useState(null);
  const [scheduledPagination, setScheduledPagination] = useState(null);
  const [completedPagination, setCompletedPagination] = useState(null);

  const [statusFilter, setStatusFilter]   = useState('Active');
  const [searchTerm, setSearchTerm]       = useState(prefilledSearch || '');
  const [expandedId, setExpandedId]       = useState(null);
  const [expandedSession, setExpandedSession] = useState(null);
  const [currentPage, setCurrentPage]     = useState(1);

  const [uploadStatsMap, setUploadStatsMap] = useState({});

  useEffect(() => {
    if (prefilledSearch) {
      setSearchTerm(prefilledSearch);
      setCurrentPage(1);
    }
  }, [prefilledSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const [confirmingStop, setConfirmingStop] = useState(false);
  const [stopMessage, setStopMessage]       = useState('');
  const [cancelTarget, setCancelTarget] = useState(null);

  // Load counts for all three tabs (size=1, read total only)
  const loadCounts = useCallback(async () => {
    const [activeResult, completedResult, scheduledResult] = await Promise.all([
      realTimeDeviceService.getActiveSessionsPaged(0, 1).catch(() => null),
      realTimeDeviceService.getCompletedSessionsPaged(0, 1).catch(() => null),
      realTimeDeviceService.getScheduledSessionsPaged(1, 1).catch(() => null),
    ]);
    if (activeResult) setActivePagination({ totalElements: activeResult.total, totalPages: Math.ceil(activeResult.total / PAGE_SIZE) || 1 });
    if (completedResult) setCompletedPagination({ totalElements: completedResult.total, totalPages: Math.ceil(completedResult.total / PAGE_SIZE) || 1 });
    if (scheduledResult) setScheduledPagination({ totalElements: scheduledResult.total, totalPages: Math.ceil(scheduledResult.total / PAGE_SIZE) || 1 });
  }, []);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  // Load the selected tab's current page
  const loadSessions = useCallback(async (tab, page) => {
    setLoading(true);
    try {
      if (tab === 'Active') {
        const result = await realTimeDeviceService.getActiveSessionsPaged(page - 1, PAGE_SIZE).catch(() => null);
        setActiveSessions(result?.sessions || []);
        if (result) setActivePagination({ totalElements: result.total, totalPages: Math.ceil(result.total / PAGE_SIZE) || 1 });
      } else if (tab === 'Completed') {
        const result = await realTimeDeviceService.getCompletedSessionsPaged(page - 1, PAGE_SIZE).catch(() => null);
        setCompletedSessions(result?.sessions || []);
        if (result) setCompletedPagination({ totalElements: result.total, totalPages: Math.ceil(result.total / PAGE_SIZE) || 1 });
      } else {
        const result = await realTimeDeviceService.getScheduledSessionsPaged(page, PAGE_SIZE).catch(() => null);
        setScheduledSessions(result?.sessions || []);
        if (result) setScheduledPagination({ totalElements: result.total, totalPages: Math.ceil(result.total / PAGE_SIZE) || 1 });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessions(statusFilter, currentPage);
  }, [statusFilter, currentPage, loadSessions]);

  const currentSessions = statusFilter === 'Active' ? activeSessions
    : statusFilter === 'Completed' ? completedSessions
    : scheduledSessions;

  // Client-side search filter over the current page (no server-side search on RT endpoints)
  const pageItems = searchTerm
    ? currentSessions.filter(s => (s.systemId || '').toLowerCase().includes(searchTerm.toLowerCase()))
    : currentSessions;

  const taggedItems = pageItems.map(s => ({ ...s, _status: statusFilter }));

  const totalPages = (
    statusFilter === 'Active' ? activePagination?.totalPages :
    statusFilter === 'Completed' ? completedPagination?.totalPages :
    scheduledPagination?.totalPages
  ) ?? 1;

  const renderPageNumbers = () => {
    const maxPagesToShow = 6;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = startPage + maxPagesToShow - 1;
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button key={i} onClick={() => setCurrentPage(i)} className={currentPage === i ? 'active' : ''}>
          {i}
        </button>
      );
    }
    return (
      <>
        <button onClick={() => setCurrentPage(1)}>&lt;&lt;</button>
        {pages}
        <button onClick={() => setCurrentPage(totalPages)}>&gt;&gt;</button>
      </>
    );
  };

  const handleRowClick = async (session) => {
    if (expandedId === session.sessionId) {
      setExpandedId(null);
      setExpandedSession(null);
      return;
    }
    if (session._status === 'Active') {
      const full = await realTimeDeviceService.getSession(session.sessionId, session.systemId).catch(() => null);
      setExpandedSession(full || session);
    } else {
      setExpandedSession(session);
    }
    setExpandedId(session.sessionId);
    if (session._status === 'Active' || session._status === 'Completed') {
      const id = session.sessionId;
      if (!uploadStatsMap[id]) {
        fetchData(`/api/v1/diagnostic-data-upload-stats?sessionId=${id}`)
          .then(res => {
            if (res?.statusCode === 200 && res.fileUploadStats) {
              setUploadStatsMap(prev => ({ ...prev, [id]: res.fileUploadStats }));
            }
          })
          .catch(() => {});
      }
    }
  };

  const handleStopConfirm = async () => {
    setConfirmingStop(false);
    await realTimeDeviceService.stopSession(expandedSession.sessionId, expandedSession.systemId).catch(() => {});
    setStopMessage('Session stopped successfully.');
    await loadSessions(statusFilter, currentPage);
    await loadCounts();
  };

  const handleCancelScheduled = async () => {
    const id = cancelTarget?.scheduledSessionId || cancelTarget?.sessionId;
    if (!id) return;
    await realTimeDeviceService.cancelScheduledSession(id).catch(() => {});
    setCancelTarget(null);
    await loadSessions(statusFilter, currentPage);
    await loadCounts();
  };

  const handleFilterClick = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
    setExpandedId(null);
    setExpandedSession(null);
  };

  const counts = {
    Active:    activePagination?.totalElements ?? activeSessions.length,
    Scheduled: scheduledPagination?.totalElements ?? scheduledSessions.length,
    Completed: completedPagination?.totalElements ?? completedSessions.length,
  };

  return (
    <div className="nd-sv-root">

      {/* ── Stat cards ── */}
      <div className="nd-sv-stats">
        {['Active', 'Scheduled', 'Completed'].map(st => (
          <div
            key={st}
            className={`nd-sv-stat-card${statusFilter === st ? ' nd-sv-stat-card--active' : ''}`}
            onClick={() => handleFilterClick(st)}
          >
            <div className="nd-sv-stat-n">{loading && statusFilter === st ? '…' : counts[st]}</div>
            <div className="nd-sv-stat-label">{STATUS_META[st].icon} {st}</div>
          </div>
        ))}
      </div>

      {/* ── Filter bar ── */}
      <div className="nd-sv-filter-bar">
        <div className="nd-sv-status-pills">
          {ALL_STATUSES.map(st => (
            <button
              key={st}
              className={`nd-sv-pill${statusFilter === st ? ' nd-sv-pill--active' : ''}`}
              onClick={() => handleFilterClick(st)}
            >
              {st}
            </button>
          ))}
        </div>
        <div className="nd-sv-search">
          <span className="nd-search-icon">🔍</span>
          <input
            className="nd-search-input"
            type="text"
            placeholder="Search by System ID…"
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          />
          {searchTerm && (
            <button className="nd-search-clear" onClick={() => { setSearchTerm(''); setCurrentPage(1); }}>✕</button>
          )}
        </div>
        <button className="nd-sv-start-btn" onClick={onStartSession}>+ Start Session</button>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="nd-no-data">Loading sessions…</div>
      ) : taggedItems.length === 0 ? (
        <div className="nd-sv-empty">
          <div className="nd-sv-empty-icon">📋</div>
          <div className="nd-sv-empty-title">No {statusFilter.toLowerCase()} sessions found</div>
          <div className="nd-sv-empty-hint">
            {statusFilter === 'Active' ? 'Start a new RT session to see it here.' :
             statusFilter === 'Scheduled' ? 'Schedule a session from the Start tab.' :
             'Completed sessions will appear here once they finish.'}
          </div>
        </div>
      ) : (
        <>
          <div className="nd-sv-table-container"><div className="nd-sv-table-wrapper">
            <table className="nd-sv-table">
              <thead>
                <tr>
                  <th style={{ width: '22%' }}>System ID</th>
                  <th style={{ width: '30%' }}>Session ID</th>
                  <th style={{ width: '20%' }}>Start / Scheduled Time</th>
                  <th style={{ width: '10%' }}>Duration</th>
                  <th style={{ width: '12%' }}>Actions</th>
                  <th style={{ width: '6%' }}></th>
                </tr>
              </thead>
              <tbody>
                {taggedItems.map(session => (
                  <React.Fragment key={session.sessionId}>
                    <tr
                      className={`nd-sv-row${expandedId === session.sessionId ? ' expanded-row' : ''}`}
                      data-status={session._status}
                      onClick={() => handleRowClick(session)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="nd-system-id" title={session.systemId}>{session.systemId}</td>
                      <td title={session.sessionId}>
                        <span className="clickable-session-id" style={{ color: '#1a4fba', textDecoration: 'underline' }}>
                          {session.sessionId}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: '#475569' }}>
                        {session._status === 'Scheduled'
                          ? formatLocalDateTime(session.scheduledTime)
                          : formatLocalDateTime(session.startTime)}
                      </td>
                      <td>{session.durationMins ? `${session.durationMins} min` : '—'}</td>
                      <td><ConfigChips session={session} /></td>
                      <td onClick={e => e.stopPropagation()}>
                        {session._status === 'Scheduled' && (
                          <button
                            className="nd-sv-row-action nd-sv-row-action--cancel"
                            onClick={() => setCancelTarget(session)}
                          >
                            Cancel
                          </button>
                        )}
                        {session._status === 'Active' && (
                          <button
                            className="nd-sv-row-action nd-sv-row-action--stop"
                            onClick={async () => {
                              const full = await realTimeDeviceService.getSession(session.sessionId, session.systemId).catch(() => session);
                              setExpandedSession(full || session);
                              setExpandedId(session.sessionId);
                              setConfirmingStop(true);
                            }}
                          >
                            Stop
                          </button>
                        )}
                      </td>
                    </tr>

                    {/* ── Inline expanded row ── */}
                    {expandedId === session.sessionId && expandedSession && (
                      <tr className="expanded-details-row">
                        <td colSpan={6}>
                          <div className="expanded-row-content nd-sv-expanded">
                            {session._status === 'Active' && (
                              <ActiveSessionDetail
                                session={expandedSession}
                                confirmingStop={confirmingStop}
                                setConfirmingStop={setConfirmingStop}
                                onSessionChanged={setExpandedSession}
                                onClose={() => { setExpandedId(null); setExpandedSession(null); loadSessions(statusFilter, currentPage); }}
                                uploadStats={uploadStatsMap[session.sessionId] || null}
                              />
                            )}
                            {session._status === 'Scheduled' && (
                              <ScheduledSessionDetail session={expandedSession} />
                            )}
                            {(session._status === 'Completed' || session._status === 'Cancelled') && (
                              <CompletedSessionDetail
                                session={expandedSession}
                                uploadStats={uploadStatsMap[session.sessionId] || null}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div></div>
          {totalPages > 1 && (
            <div className="pagination-controls">{renderPageNumbers()}</div>
          )}
        </>
      )}

      {stopMessage && (
        <Popup message={stopMessage} onClose={() => { setStopMessage(''); setExpandedId(null); setExpandedSession(null); }} />
      )}

      {cancelTarget && (
        <ConfirmationDialog
          message={`Cancel the scheduled session for device ${cancelTarget.systemId}?`}
          onConfirm={handleCancelScheduled}
          onCancel={() => setCancelTarget(null)}
        />
      )}

      {confirmingStop && expandedSession && (
        <ConfirmationDialog
          message={`Stop the active session for device ${expandedSession.systemId}?`}
          onConfirm={handleStopConfirm}
          onCancel={() => setConfirmingStop(false)}
        />
      )}
    </div>
  );
}

// ── Inline detail panels ────────────────────────────────────────────────────

function UploadStatsPanel({ uploadStats }) {
  if (!uploadStats) return <div className="nd-sv-stats-panel"><span className="nd-sv-loading-hint">Loading…</span></div>;
  return (
    <div className="nd-sv-stats-panel">
      <div className="nd-sv-stats-panel-counts">
        <div className="nd-sv-stats-panel-item nd-sv-stats-panel-item--files">
          <span className="nd-sv-stats-panel-value">{uploadStats.filesUploadedCount ?? 0}</span>
          <span className="nd-sv-stats-panel-label">Files Uploaded</span>
        </div>
        {uploadStats.uploadBaseUrl && (
          <div className="nd-sv-stats-panel-item nd-sv-stats-panel-item--url">
            <span className="nd-sv-stats-panel-url-value">{uploadStats.uploadBaseUrl}</span>
            <span className="nd-sv-stats-panel-label">Upload Base URL</span>
          </div>
        )}
      </div>
    </div>
  );
}

function ActiveSessionDetail({ session, confirmingStop, setConfirmingStop, onSessionChanged, onClose, uploadStats }) {
  const [elapsedSec, setElapsedSec] = useState(
    session.startTime ? Math.floor((Date.now() - new Date(session.startTime).getTime()) / 1000) : 0
  );
  useEffect(() => {
    const t = setInterval(() => setElapsedSec(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [session.sessionId]);

  const [localActions, setLocalActions] = useState(session.actions || []);
  const [showReview, setShowReview] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState('');
  const [showUploadStats, setShowUploadStats] = useState(false);

  const originalIds = new Set((session.actions || []).map(a => a.id));
  const localIds = new Set(localActions.map(a => a.id));
  const added   = localActions.filter(a => !originalIds.has(a.id));
  const removed = (session.actions || []).filter(a => !localIds.has(a.id));
  const hasPendingChanges = added.length > 0 || removed.length > 0;

  const handleDiscard = () => { setLocalActions(session.actions || []); setApplyError(''); };

  const handleApply = async () => {
    setApplying(true);
    setApplyError('');
    try {
      const updated = await realTimeDeviceService.replaceActions(
        session.sessionId, session.systemId, localActions, session
      );
      setShowReview(false);
      if (updated) onSessionChanged({ ...updated, actions: localActions });
    } catch (e) {
      setApplyError('Failed to apply changes. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  const handleAddAction    = (action)          => setLocalActions(prev => [...prev, action]);
  const handleUpdateAction = (actionId, fields) => setLocalActions(prev => prev.map(a => a.id === actionId ? { ...a, ...fields, id: actionId } : a));
  const handleRemoveAction = (actionId)         => setLocalActions(prev => prev.filter(a => a.id !== actionId));

  const ACTION_ICONS_MAP = { monitor: '👁️', configure: '⚙️', run: '⚡' };

  return (
    <div>
      <div className="nd-sv-detail-header">
        <div className="nd-sv-detail-meta">
          <span>Device: <strong>{session.systemId}</strong></span>
          <span>Session: <strong>{session.sessionId}</strong></span>
          <span>⏱ <strong>{formatElapsed(elapsedSec)}</strong> / {session.durationMins ? `${session.durationMins}:00` : '∞'}</span>
          {session.configVersion != null && <span>Config Version: <strong>v{session.configVersion}</strong></span>}
        </div>
        <div className="nd-sv-detail-actions">
          <button className="nd-sv-stop-btn" onClick={() => setConfirmingStop(true)}>⊙ Stop Session</button>
          <button className="go-back-button" style={{ marginTop: 0 }} onClick={onClose}>✕ Close</button>
        </div>
      </div>

      {hasPendingChanges && (
        <div className="nd-sv-pending-banner">
          <span>⚠️ <strong>{added.length + removed.length} unsaved change{added.length + removed.length !== 1 ? 's' : ''}</strong> — not yet sent to device</span>
          <div className="nd-sv-pending-banner-actions">
            <button className="nd-sv-pending-discard" onClick={handleDiscard}>Discard</button>
            <button className="nd-sv-pending-review" onClick={() => { setApplyError(''); setShowReview(true); }}>Review &amp; Apply</button>
          </div>
        </div>
      )}

      {applyError && (
        <div className="nd-sv-error-banner">
          <span>{applyError}</span>
          <button className="nd-sv-error-close" onClick={() => setApplyError('')}>✕</button>
        </div>
      )}

      <RTActionEditor
        actions={localActions}
        onAdd={handleAddAction}
        onRemove={handleRemoveAction}
        onUpdate={handleUpdateAction}
        variant="active"
      />

      {/* Upload Statistics toggle */}
      <div className="nd-sv-action-links" style={{ marginTop: 12 }}>
        <button
          className={`nd-sv-action-link${showUploadStats ? ' nd-sv-action-link--active' : ''}`}
          onClick={() => setShowUploadStats(v => !v)}
        >
          📊 {showUploadStats ? 'Hide Upload Statistics' : 'Upload Statistics'}
        </button>
      </div>
      {showUploadStats && <UploadStatsPanel uploadStats={uploadStats} />}

      {/* Review & Apply modal */}
      {showReview && (
        <div className="nd-sv-review-overlay" onClick={e => { if (e.target === e.currentTarget) setShowReview(false); }}>
          <div className="nd-sv-review-modal">
            <div className="nd-sv-review-modal-header">
              <div>
                <h3>Review Pending Changes</h3>
                <p>{added.length + removed.length} change{added.length + removed.length !== 1 ? 's' : ''} will be sent to the device</p>
              </div>
              <button className="nd-sv-review-close" onClick={() => setShowReview(false)}>✕</button>
            </div>
            <div className="nd-sv-review-modal-body">
              {added.length > 0 && (
                <div>
                  <div className="nd-sv-review-section-label nd-sv-review-section-label--add">➕ Adding ({added.length})</div>
                  {added.map(a => (
                    <div key={a.id} className="nd-sv-review-row nd-sv-review-row--add">
                      <span className="nd-sv-review-row-icon">{a.icon || ACTION_ICONS_MAP[a.type]}</span>
                      <span className="nd-sv-review-row-label">{a.label}</span>
                      {a.sub && <span className="nd-sv-review-row-sub">{a.sub}</span>}
                    </div>
                  ))}
                </div>
              )}
              {removed.length > 0 && (
                <div>
                  <div className="nd-sv-review-section-label nd-sv-review-section-label--remove">➖ Removing ({removed.length})</div>
                  {removed.map(a => (
                    <div key={a.id} className="nd-sv-review-row nd-sv-review-row--remove">
                      <span className="nd-sv-review-row-icon">{a.icon || ACTION_ICONS_MAP[a.type]}</span>
                      <span className="nd-sv-review-row-label">{a.label}</span>
                      {a.sub && <span className="nd-sv-review-row-sub">{a.sub}</span>}
                    </div>
                  ))}
                </div>
              )}
              {applyError && <div className="nd-sv-error-banner"><span>{applyError}</span></div>}
            </div>
            <div className="nd-sv-review-modal-footer">
              <button className="nd-sv-review-cancel" onClick={() => setShowReview(false)} disabled={applying}>Cancel</button>
              <button className="nd-sv-review-apply" onClick={handleApply} disabled={applying}>
                {applying ? 'Applying…' : 'Apply Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ScheduledSessionDetail({ session }) {
  return (
    <div className="nd-sv-detail-grid">
      <div className="nd-sv-detail-item"><span>Session ID</span><strong>{session.sessionId}</strong></div>
      <div className="nd-sv-detail-item"><span>System ID</span><strong>{session.systemId}</strong></div>
      <div className="nd-sv-detail-item"><span>Scheduled Time</span><strong>{formatLocalDateTime(session.scheduledTime)}</strong></div>
      <div className="nd-sv-detail-item"><span>Duration</span><strong>{session.durationMins ? `${session.durationMins} min` : '—'}</strong></div>
      <div className="nd-sv-detail-item"><span>Scheduling Mode</span><strong>{session.schedulingMode || '—'}</strong></div>
      <div className="nd-sv-detail-item"><span>Created By</span><strong>{session.scheduledBy || session.createdUser || '—'}</strong></div>
    </div>
  );
}

function CompletedSessionDetail({ session, uploadStats }) {
  const TYPE_ROW_CLASS = { monitor: 'rt-action-row-monitor', configure: 'rt-action-row-configure', run: 'rt-action-row-run' };
  const ACTION_ICONS   = { monitor: '👁️', configure: '⚙️', run: '⚡' };
  const [showUploadStats, setShowUploadStats] = useState(false);
  return (
    <div>
      <div className="expanded-details-cards expanded-details-row-1">
        <div className="expanded-detail-card">
          <h4>Session Info</h4>
          <div className="expanded-detail-card-content">
            <div className="expanded-detail-item"><strong>Session ID:</strong> {session.sessionId}</div>
            <div className="expanded-detail-item"><strong>System ID:</strong> {session.systemId}</div>
            <div className="expanded-detail-item"><strong>Duration:</strong> {session.durationMins ? `${session.durationMins} min` : '—'}</div>
            <div className="expanded-detail-item"><strong>Start Time:</strong> {formatLocalDateTime(session.startTime)}</div>
            <div className="expanded-detail-item"><strong>End Time:</strong> {formatLocalDateTime(session.endTime)}</div>
            <div className="expanded-detail-item"><strong>Termination:</strong> {session.termination || '—'}</div>
            <div className="expanded-detail-item"><strong>Created By:</strong> {session.createdUser || '—'}</div>
            {session.configVersion != null && (
              <div className="expanded-detail-item"><strong>Config Version:</strong> v{session.configVersion}</div>
            )}
          </div>
        </div>
        <div className="expanded-detail-card">
          <h4>Configured Actions ({(session.actions || []).length})</h4>
          <div className="expanded-detail-card-content">
            {(session.actions || []).length === 0 ? (
              <span className="rt-text-muted">No actions were configured.</span>
            ) : (
              <div className="rt-configured-actions">
                {(session.actions || []).map((a, idx) => (
                  <div key={a.id || idx} className={`rt-action-item ${TYPE_ROW_CLASS[a.type] || ''}`}>
                    <span className={`rt-action-icon ${a.type}`}>{a.icon || ACTION_ICONS[a.type]}</span>
                    <div style={{ flex: 1 }}>
                      <div className="rt-action-label">{a.label} <span className="rt-text-muted" style={{ fontWeight: 400 }}>#{idx + 1}</span></div>
                      <div className="rt-action-sub">Type: {a.type} · {a.sub || a.key}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="nd-sv-action-links" style={{ marginTop: 12 }}>
        <button
          className={`nd-sv-action-link${showUploadStats ? ' nd-sv-action-link--active' : ''}`}
          onClick={() => setShowUploadStats(v => !v)}
        >
          📊 {showUploadStats ? 'Hide Upload Statistics' : 'Upload Statistics'}
        </button>
      </div>
      {showUploadStats && <UploadStatsPanel uploadStats={uploadStats} />}
    </div>
  );
}

export default NdRtViewSessions;
